import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { trackAuth, setUserProperties } from '../utils/googleAnalyticsService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  
  // CRITICAL FIX: Promise resolver to track logout completion
  const logoutResolverRef = React.useRef(null)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) {
          setUserProfile(data)
          
          // Track user properties in Google Analytics
          setUserProperties({
            user_id: userId,
            user_type: data?.role || 'user',
            has_trips: data?.trip_count > 0,
          });
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      
      // Clear user properties in Google Analytics
      setUserProperties({
        user_id: null,
        user_type: null,
      });
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      // CRITICAL FIX: Resolve logout promise when session becomes null
      if (!session?.user && logoutResolverRef?.current) {
        logoutResolverRef?.current()
        logoutResolverRef.current = null
      }
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check - IMPROVED error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession()
        
        if (error) {
          console.error('Error getting initial session:', error)
          setLoading(false)
          return
        }
        
        authStateHandlers?.onChange(null, session)
      } catch (error) {
        console.error('Error initializing auth:', error)
        setLoading(false)
      }
    }

    initializeAuth()

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      // Track successful login in Google Analytics
      if (!error && data?.user) {
        trackAuth('login', 'email', {
          user_id: data?.user?.id,
        });
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Track logout before clearing state
      if (user?.id) {
        trackAuth('logout', 'email', {
          user_id: user?.id,
        });
      }
      
      // CRITICAL FIX: Clear ALL state IMMEDIATELY before Supabase call
      // This prevents any component from seeing stale auth data
      setUser(null)
      setUserProfile(null)
      setLoading(true)
      
      // Call Supabase signOut
      const { error } = await supabase?.auth?.signOut()
      
      if (error) {
        // On error, restore state from Supabase
        const { data: { session } } = await supabase?.auth?.getSession()
        setUser(session?.user ?? null)
        if (session?.user) {
          profileOperations?.load(session?.user?.id)
        } else {
          setUserProfile(null)
        }
        setLoading(false)
        return { error }
      }
      
      // CRITICAL FIX: Add small delay to ensure Supabase state is fully cleared
      // This prevents race condition where page reloads before auth state is cleared
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // CRITICAL FIX: Clear all Supabase-related items from localStorage
      // This ensures no stale auth data remains in browser storage
      const keysToRemove = Object.keys(localStorage)?.filter(key => 
        key?.startsWith('sb-') || key?.includes('supabase')
      )
      keysToRemove?.forEach(key => localStorage.removeItem(key))
      
      // CRITICAL FIX: Force complete app reload using window.location
      // This ensures ALL components re-mount with fresh Supabase auth state
      // React Router navigation was causing stale state to persist
      window.location.href = '/'
      
      return { error: null }
    } catch (error) {
      // On exception, restore state from Supabase
      const { data: { session } } = await supabase?.auth?.getSession()
      setUser(session?.user ?? null)
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        setUserProfile(null)
      }
      setLoading(false)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}